/**
 * Page de signature — /signer/:token — M1 (retour Manon, mi-parcours).
 *
 * L'adresse était générée par le moteur depuis la mig 241 ; la page n'existait
 * pas. Publique (le signataire peut ne pas avoir de compte : l'association à
 * qui l'on remet une clé) : le jeton fait office d'autorisation, pour CE seul
 * document. On RELIT le document avant de signer, puis on trace ou on tape
 * son nom. Mention honnête : signature électronique simple, pas qualifiée.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SignaturePad } from '@/components/signature/SignaturePad';

interface Info { documentTitre: string; signataireNom: string; statut: string; signable: boolean; raison: string | null; signeAt: string | null }

export function SignerPage() {
  const { token = '' } = useParams();
  const [info, setInfo] = useState<Info | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [mode, setMode] = useState<'drawn' | 'typed'>('drawn');
  const [trace, setTrace] = useState<string | null>(null);
  const [nom, setNom] = useState('');
  const [lu, setLu] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [fait, setFait] = useState<{ empreinte: string; signeAt: string } | null>(null);

  useEffect(() => {
    fetch(`/api/public/signature/${encodeURIComponent(token)}`)
      .then(async (r) => (r.ok ? setInfo(await r.json()) : setErr((await r.json().catch(() => null))?.error ?? 'Lien de signature inconnu')))
      .catch(() => setErr('Service indisponible, réessayez plus tard.'));
  }, [token]);

  async function signer() {
    setEnvoi(true); setErr(null);
    try {
      const r = await fetch(`/api/public/signature/${encodeURIComponent(token)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mode === 'drawn' ? { kind: 'drawn', imageDataUrl: trace } : { kind: 'typed', typedNom: nom.trim() }),
      });
      const j = await r.json().catch(() => null);
      if (!r.ok) throw new Error(j?.error ?? 'Signature impossible');
      setFait(j);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Signature impossible'); }
    finally { setEnvoi(false); }
  }

  const peutSigner = lu && (mode === 'drawn' ? !!trace : nom.trim().length >= 2) && !envoi;

  return (
    <main id="main-content" className="mx-auto max-w-xl px-4 py-8">
      <h1 className="font-display text-xl font-semibold text-ink">Signature d'un document</h1>
      {err && <p className="mt-3 rounded-r-md border border-urgent/40 p-3 text-sm text-urgent" role="alert">{err}</p>}
      {!info && !err && <p className="mt-3 text-sm text-ink-muted">Chargement…</p>}

      {info && fait && (
        <div className="mt-4 rounded-r-lg border border-success/40 p-4" role="status">
          <p className="font-medium text-ink">Document signé. Merci.</p>
          <p className="mt-1 text-xs text-ink-muted">Signé le {new Date(fait.signeAt).toLocaleString('fr-FR')} · empreinte {fait.empreinte}</p>
        </div>
      )}

      {info && !fait && (
        <div className="mt-4 space-y-4">
          <div className="rounded-r-lg border border-border p-4">
            <p className="text-sm text-ink-muted">Document</p>
            <p className="font-medium text-ink">{info.documentTitre}</p>
            <p className="mt-2 text-sm text-ink-muted">Signataire : <b className="text-ink">{info.signataireNom}</b></p>
          </div>

          {!info.signable ? (
            <p className="text-sm text-ink-muted">{info.raison}</p>
          ) : (
            <>
              <a className="inline-block text-sm font-medium text-accent underline" target="_blank" rel="noopener noreferrer"
                href={`/api/public/signature/${encodeURIComponent(token)}/document`} onClick={() => setLu(true)}>
                Lire le document avant de signer (PDF)
              </a>
              <label className="flex items-start gap-2 text-sm text-ink">
                <input type="checkbox" className="mt-1" checked={lu} onChange={(e) => setLu(e.target.checked)} />
                J'ai pris connaissance du document.
              </label>

              <div className="flex gap-2 text-sm" role="radiogroup" aria-label="Manière de signer">
                <button type="button" role="radio" aria-checked={mode === 'drawn'} onClick={() => setMode('drawn')}
                  className={`rounded-r-md border px-3 py-1.5 ${mode === 'drawn' ? 'border-accent font-medium' : 'border-border text-ink-muted'}`}>Signer au doigt</button>
                <button type="button" role="radio" aria-checked={mode === 'typed'} onClick={() => setMode('typed')}
                  className={`rounded-r-md border px-3 py-1.5 ${mode === 'typed' ? 'border-accent font-medium' : 'border-border text-ink-muted'}`}>Taper mon nom</button>
              </div>

              {mode === 'drawn'
                ? <SignaturePad onChange={setTrace} />
                : <input className="h-11 w-full rounded-r-md border border-border bg-surface px-3" aria-label="Votre nom"
                    placeholder="Prénom Nom" value={nom} maxLength={160} onChange={(e) => setNom(e.target.value)} />}

              <button type="button" disabled={!peutSigner} onClick={() => void signer()}
                className="h-11 w-full rounded-r-lg bg-accent font-medium text-white disabled:opacity-40">
                {envoi ? 'Signature…' : 'Signer le document'}
              </button>
              <p className="text-2xs text-ink-subtle">
                Signature VPC — signature électronique simple (article 1367 du code civil) : la date, l'heure et l'empreinte du document
                sont enregistrées. Il ne s'agit pas d'une signature qualifiée.
              </p>
            </>
          )}
        </div>
      )}
    </main>
  );
}
